/**
 * RogueWorlds client - runtime orchestration only.
 *
 * Boot order (each step exists before the next one needs it):
 *   canvas -> WebGLRenderer -> GET /api/config -> Scene -> atlas + ChunkRenderer + PlayerRenderer +
 *   MesherPool -> NetClient -> single rAF loop.
 *
 * Update/render order, once per frame, in this exact order (one rAF owner, one clamped delta):
 *   1. resize (pure, returns whether anything changed)
 *   2. input   : frame-rate accumulation -> one 10 Hz `input` frame -> becomes the current command
 *   3. predict : fixed-step accumulator, `stepPlayer` exactly `tickHz` times per second per step
 *   4. stream  : ClientWorld streaming scan + bounded drain of the received-chunk mailbox
 *   5. remote  : interpolate buffered snapshots (~150 ms behind) onto PlayerRenderer
 *   6. camera  : predicted eye position + yaw/pitch, sun follows the view
 *   7. HUD     : dirty-flag, internally throttled to 4 Hz
 *   8. render  : a single `renderer.render(scene, camera)`
 *
 * Nothing else in the client owns a `requestAnimationFrame`, and the local player is NEVER positioned
 * from a `snapshot`: only `ack` reconciliation moves the simulation.
 */

import * as THREE from "three";
import {
  DEFAULT_WORLD_CONFIG,
  PROTOCOL_VERSION,
  VOXEL_SIZE_M,
  type BlockId,
  type ChunkSnapshot,
  type ClientMessage,
  type Vec3,
  type WorldConfig,
} from "@rogueworlds/shared";
import { PLAYER_HEIGHT_M, PLAYER_RADIUS_M, type PlayerCommand } from "@rogueworlds/sim";
import { Hud } from "./hud";
import { InputController, lookDirection, type InputSample } from "./input";
import { MesherPool } from "./mesh/mesher-pool";
import { NetClient, type NetStatus } from "./net/client";
import { Prediction } from "./prediction";
import { createAtlasTexture } from "./render/atlas";
import { ChunkRenderer } from "./render/chunk-renderer";
import { PlayerRenderer } from "./render/players";
import { createRenderer, disposeRenderer, resizeRenderer } from "./render/renderer";
import { createScene, followSun, type SceneHandles } from "./render/scene";
import { AIR, CHUNK_EDGE_M } from "./world/bridge";
import { ClientWorld, shouldRebuildWorldFor } from "./world/client-world";
import { HOTBAR_BLOCKS, REACH_M, raycastVoxels, type RayHit } from "./world/interaction";
import { RemotePlayers } from "./world/remote-players";

/** Hard clamp on a frame delta: a tab wake-up must not teleport the world. */
const MAX_FRAME_DELTA_S = 0.1;
/** Fixed steps per frame ceiling (the clamp above allows at most 2 at 20 Hz). */
const MAX_STEPS_PER_FRAME = 5;
/** Received-chunk mailbox drain per frame: bounds main-thread resync cost. */
const MAX_CHUNK_APPLIES_PER_FRAME = 3;
/** Mailbox cap; a flood is a server bug, not a reason to run out of memory. */
const CHUNK_MAILBOX_LIMIT = 1024;
/** FPS smoothing factor. */
const FPS_SMOOTHING = 0.1;
/** Local dev fallback when `/api/config` is unreachable (e.g. `vite dev` without the gateway). */
const DEV_FALLBACK_ENDPOINT = "ws://localhost:2567";
/**
 * `error` codes the authority uses for conditions a running client cannot recover from: it refuses
 * our protocol revision, it hit an internal fault, or it sent a frame no client can act on. Every
 * other code (`rate-limit`, `interest-limit`, `too-many-messages`, `sub-too-many-keys`, and the
 * edit-repair codes that are paired with an `ok:false` `blockAck`) is momentary and self-correcting -
 * the two that refuse `sub` KEYS are repaired below, by re-requesting the refused batch.
 */
const FATAL_ERROR_CODES: ReadonlySet<string> = new Set(["protocol-version", "internal", "bad-request"]);
/**
 * `error` codes that mean "keys of a `sub` frame were refused": `too-many-messages` is a `sub` the
 * inbound work budget cannot afford and refuses that frame WHOLE, while `interest-limit` is a residency
 * overflow, which the authority refuses only IN PART - it serves every key that still fits and skips
 * the rest (`apps/server/src/world/room.ts` `onSub`). Neither code names keys, so the client must undo
 * the local `subscribed` marks of the frame it sent last; that repair is approximate (see
 * `ClientWorld.forgetRefusedSubBatch`).
 */
const REFUSED_SUB_CODES: ReadonlySet<string> = new Set(["too-many-messages", "interest-limit"]);

interface RemoteConfig {
  protocolVersion: number;
  world: WorldConfig;
  serverEndpoint: string;
}

const FALLBACK_CONFIG: RemoteConfig = {
  protocolVersion: PROTOCOL_VERSION,
  world: DEFAULT_WORLD_CONFIG,
  serverEndpoint: DEV_FALLBACK_ENDPOINT,
};

async function main(): Promise<void> {
  const canvas = requireElement<HTMLCanvasElement>("game");
  const appRoot = requireElement<HTMLElement>("app");

  let net: NetClient | undefined;
  const hud = new Hud(appRoot, () => {
    net?.connect();
  });
  hud.setOverlay({
    title: "Loading",
    detail: "Creating the renderer and reading /api/config ...",
    error: false,
    retry: false,
  });
  hud.setHotbar(HOTBAR_BLOCKS, 0);

  const renderer = createRenderer(canvas);
  const config = await loadConfig();
  hud.setOverlay({
    title: "Connecting",
    detail: `World endpoint ${config.serverEndpoint}`,
    error: false,
    retry: false,
  });

  const stage: SceneHandles = createScene(aspectOf(canvas));
  const atlas = createAtlasTexture();
  const chunkRenderer = new ChunkRenderer(stage.scene, atlas);
  const playerRenderer = new PlayerRenderer(stage.scene);
  const pool = new MesherPool();

  // Mutable runtime state; `world`/`prediction`/`remote` only exist after `welcome`.
  let world: ClientWorld | undefined;
  let prediction: Prediction | undefined;
  let remote: RemotePlayers | undefined;
  let playerId = "";
  let viewDistanceChunks = config.world.viewDistanceChunks;
  let tickHz = config.world.tickHz;
  let serverTick = 0;
  let selectedSlot = 0;
  let running = true;
  let rafId = 0;

  const mailbox: ChunkSnapshot[] = [];
  const eyeScratch: Vec3 = { x: 0, y: 0, z: 0 };
  const feetScratch: Vec3 = { x: 0, y: 0, z: 0 };
  const directionScratch: Vec3 = { x: 0, y: 0, z: -1 };
  const hitScratch: RayHit = { bx: 0, by: 0, bz: 0, nx: 0, ny: 0, nz: 0, block: 0, distance: 0 };
  const sunTarget = new THREE.Vector3();
  /** Neutral command: no intent, current look, gravity only. Used until the first input sample. */
  let command: PlayerCommand = {
    seq: 0,
    dt: 0,
    move: { x: 0, y: 0, z: 0 },
    look: { x: 0, y: 0, z: 0 },
    actions: 0,
  };

  const input = new InputController(canvas, {
    onInteract: (button) => {
      interact(button);
    },
    onSelectSlot: (index) => {
      selectedSlot = index < HOTBAR_BLOCKS.length ? index : selectedSlot;
      hud.setHotbar(HOTBAR_BLOCKS, selectedSlot);
    },
  });
  input.attach();

  net = new NetClient({
    endpoint: config.serverEndpoint,
    name: playerName(),
    onStatus: (status: NetStatus) => {
      applyStatus(status);
    },
  });

  net.on("welcome", (message) => {
    // `playerId` still holds the PREVIOUS session's id here: `""` means no world exists yet.
    const previousPlayerId = playerId;
    if (message.config.tickHz > 0) {
      tickHz = message.config.tickHz;
    }
    if (message.config.viewDistanceChunks > 0) {
      viewDistanceChunks = message.config.viewDistanceChunks;
    }
    playerId = message.playerId;
    // The authority delivers `welcome` TWICE on every join (from `onJoin`, then again when our `hello`
    // arrives) with the same `playerId`, so a re-delivery must not rebuild the live world: doing so
    // re-sends `sub` for the entire view at once, the authority refuses a `sub` it cannot afford whole,
    // and a refused key would never be re-requested. Only an id change (a fresh join after a drop) does.
    if (!shouldRebuildWorldFor(previousPlayerId, message.playerId)) {
      return;
    }
    // A new session (a different `playerId`, i.e. a fresh join after a drop) must not leave the
    // previous world's meshes and jobs behind.
    remote?.clear();
    world?.dispose();
    world = new ClientWorld({
      seed: message.worldSeed,
      renderer: chunkRenderer,
      pool,
      // Passed through verbatim: absent means the server has no `unsub`, so we must not send one.
      capabilities: message.capabilities,
      send: (outgoing: ClientMessage) => {
        net?.send(outgoing);
      },
    });
    // The client tick axis is seeded from the server tick so `reconcile` compares like with like.
    // The SolidQuery is backed by `ClientWorld.blockAt` through the voxel registry's solidity rule.
    prediction = new Prediction(
      message.playerId,
      playerName(),
      message.spawn,
      message.tick,
      tickHz,
      world.solidAt,
    );
    remote = new RemotePlayers(playerRenderer, tickHz);
    remote.setLocalId(message.playerId);
    serverTick = message.tick;
    command = {
      seq: 0,
      dt: 0,
      move: { x: 0, y: 0, z: 0 },
      look: { x: 0, y: 0, z: 0 },
      actions: 0,
    };
    mailbox.length = 0;
    hud.setOverlay(null);
    // A fresh handshake starts a fresh diagnostic line; a throttle notice from before the reconnect
    // would otherwise linger for the rest of the session.
    hud.setNotice(null);
  });

  net.on("chunk", (message) => {
    if (mailbox.length >= CHUNK_MAILBOX_LIMIT) {
      console.warn("[client] chunk mailbox overflow; dropping the oldest snapshot");
      const dropped = mailbox.shift();
      // The dropped chunk's key was marked `subscribed` when its `sub` was sent, and only eviction ever
      // clears that mark, so no later view scan asks the authority for those bytes again: the chunk
      // would keep its generated-terrain view, missing every edit the authority had already applied to
      // it, until the player left and re-entered the eviction disc. Re-request it through the documented
      // one-key re-stream (`sub`), which the authority charges one work unit for, and which its inbound
      // rate limiter bounds however often this repeats.
      if (dropped !== undefined) {
        world?.resyncChunk(dropped.key);
      }
    }
    mailbox.push(message.snapshot);
  });

  net.on("snapshot", (message) => {
    serverTick = message.tick;
    remote?.push(message.tick, message.players, performance.now());
  });

  net.on("ack", (message) => {
    if (message.tick > serverTick) {
      serverTick = message.tick;
    }
    prediction?.onAck(message);
  });

  net.on("blockAck", (message) => {
    const liveWorld = world;
    if (liveWorld === undefined) {
      return;
    }
    // The authority fans ONE encoded `blockAck` out to the originator AND to every other client whose
    // interest set holds that chunk (`apps/server`, `onSetBlock`), so this frame is not necessarily
    // ours. Ack matching is positional (`blockAck` carries no `seq`).
    const seq = liveWorld.pendingSeqAt(message.key, message.x, message.y, message.z);
    // Always adopt the post-edit revision, monotonically. It is the `rev` precondition of our next
    // `setBlock`, and a token left stale by somebody else's edit is a guaranteed `stale-rev` refusal
    // on our next edit of that chunk (visible revert + a full ~40 KB chunk re-subscription).
    liveWorld.adoptRevision(message.key, message.rev);
    if (seq === undefined) {
      if (message.ok) {
        // Another player's accepted edit: apply it so their building/mining is visible now instead of
        // at the next chunk re-stream, and so collision is no longer predicted against a phantom voxel.
        liveWorld.applyRemoteEdit(message.key, message.x, message.y, message.z, message.block, message.rev);
        return;
      }
      // `ok:false` is only ever sent to the originator, so an unmatched one means our verdict raced a
      // foreign `ok:true` fan-out for the same voxel: that frame consumed the pending record by
      // position, and the rejection was then dropped. The local chunk is diverged at that voxel (our
      // prediction stays in the overlay and the pending record that would roll it back is gone), and
      // nothing else will ever repair it before eviction - so force a re-stream from the authority.
      liveWorld.resyncChunk(message.key);
      return;
    }
    if (message.ok) {
      liveWorld.confirmEdit(seq);
    } else {
      liveWorld.rejectEdit(seq, message.block);
    }
  });

  net.on("error", (message) => {
    // Only codes that mean "this session cannot continue" get the full-screen overlay, which the next
    // `welcome` is the only thing able to clear. The authority also reports ordinary, self-correcting
    // conditions (a momentary edit/throttle cap, a refused interest key, an edit the `blockAck` verdict
    // repairs); covering a running game for those is a bug, so they go to the HUD diagnostic line.
    if (FATAL_ERROR_CODES.has(message.code)) {
      hud.setOverlay({
        title: "Server error",
        detail: `${message.code}: ${message.message}`,
        error: true,
        retry: false,
      });
      return;
    }
    if (REFUSED_SUB_CODES.has(message.code)) {
      // The two codes that mean "keys of a `sub` frame were refused": the authority grants nothing for
      // a `sub` it cannot afford (`too-many-messages`), and for a residency overflow (`interest-limit`)
      // it serves every key that still fits and skips only the rest, so "refused" is not "granted
      // nothing" there. Neither message names keys. The world marked the keys of the frame it sent LAST
      // `subscribed` when it sent them, and only eviction ever clears that mark, so without this the
      // refused part of the view would never be requested again. Forgetting that batch makes the next
      // frame's view scan re-send it: a key the authority DID serve is re-requested too (one redundant
      // `sub`, idempotent on arrival), and a key an earlier refused frame of the same pass carried can
      // stay marked until re-entry - re-entry is the complete repair.
      world?.forgetRefusedSubBatch();
    }
    hud.setNotice(`${message.code}: ${message.message}`);
  });

  function applyStatus(status: NetStatus): void {
    switch (status) {
      case "connecting":
        hud.setOverlay({
          title: "Connecting",
          detail: `Waiting for ${config.serverEndpoint} (protocol v${config.protocolVersion}).`,
          error: false,
          retry: false,
        });
        break;
      case "connected":
        hud.setOverlay({
          title: "Joined",
          detail: "Waiting for the world handshake ...",
          error: false,
          retry: false,
        });
        break;
      case "disconnected":
        hud.setOverlay({
          title: "Disconnected",
          detail: "The world connection closed. Your predicted position is kept until you retry.",
          error: true,
          retry: true,
        });
        break;
      case "error":
      default:
        hud.setOverlay({
          title: "Server unreachable",
          detail:
            `Could not join the world room at ${config.serverEndpoint} over the Colyseus transport. ` +
            "For local development start the Colyseus server on 127.0.0.1:2567 (it is proxied on " +
            "/matchmake and /api). The client keeps rendering, but there is no voxel data yet.",
          error: true,
          retry: true,
        });
        break;
    }
  }

  /** Raycast from the eye and apply the edit optimistically, then send the `setBlock` intent. */
  function interact(button: "primary" | "secondary"): void {
    const liveWorld = world;
    const livePrediction = prediction;
    if (liveWorld === undefined || livePrediction === undefined) {
      return;
    }
    livePrediction.eyePosition(eyeScratch);
    lookDirection(input.yaw, input.pitch, directionScratch);
    const hit = raycastVoxels(liveWorld, eyeScratch, directionScratch, hitScratch, REACH_M);
    if (hit === undefined) {
      return;
    }
    if (button === "primary") {
      applyEdit(liveWorld, hit.bx, hit.by, hit.bz, AIR);
      return;
    }
    const bx = hit.bx + hit.nx;
    const by = hit.by + hit.ny;
    const bz = hit.bz + hit.nz;
    // Refuse to place a solid voxel inside our own collision column: the server would reject it and
    // we would only be predicting a rollback.
    if (intersectsLocalBody(bx, by, bz, livePrediction.predicted.pos)) {
      return;
    }
    applyEdit(liveWorld, bx, by, bz, HOTBAR_BLOCKS[selectedSlot] ?? 1);
  }

  function applyEdit(target: ClientWorld, bx: number, by: number, bz: number, block: BlockId): void {
    const record = target.applyLocalEdit(bx, by, bz, block);
    if (record === undefined) {
      return;
    }
    net?.send({
      t: "setBlock",
      key: record.key,
      x: record.x,
      y: record.y,
      z: record.z,
      block: record.block,
      rev: record.rev,
    });
  }

  let lastMs = performance.now();
  let accumulator = 0;
  let fps = 60;

  function frame(nowMs: number): void {
    if (!running) {
      return;
    }
    rafId = window.requestAnimationFrame(frame);
    const rawDelta = (nowMs - lastMs) / 1000;
    lastMs = nowMs;
    const delta = rawDelta > MAX_FRAME_DELTA_S ? MAX_FRAME_DELTA_S : rawDelta > 0 ? rawDelta : 0;
    if (delta > 0) {
      fps += (1 / delta - fps) * FPS_SMOOTHING;
    }

    // 1. resize (no-op unless the drawing buffer actually changed)
    resizeRenderer(renderer, stage.camera, canvas);

    // 2. input: one 10 Hz frame with the current intent becomes the current command
    const sample: InputSample | undefined = input.poll(delta);
    if (sample !== undefined) {
      net?.send(sample);
      command = sample;
    }

    // 3. prediction: fixed step at exactly the server's tick rate
    const livePrediction = prediction;
    if (livePrediction !== undefined) {
      const step = 1 / tickHz;
      accumulator += delta;
      let steps = 0;
      while (accumulator >= step && steps < MAX_STEPS_PER_FRAME) {
        livePrediction.step(command);
        accumulator -= step;
        steps += 1;
      }
      if (accumulator > step) {
        // The backlog is stale (tab wake-up / breakpoint): drop it instead of fast-forwarding.
        accumulator = 0;
      }
      livePrediction.decaySmoothing(delta);
    }

    // 4. streaming + bounded mailbox drain
    const liveWorld = world;
    if (liveWorld !== undefined && livePrediction !== undefined) {
      liveWorld.update(livePrediction.predicted.pos, viewDistanceChunks);
      let applied = 0;
      while (applied < MAX_CHUNK_APPLIES_PER_FRAME && mailbox.length > 0) {
        const snapshot = mailbox.shift();
        if (snapshot === undefined) {
          break;
        }
        liveWorld.applyServerChunk(snapshot);
        applied += 1;
      }
    }

    // 5. remote players (interpolated, never the local player)
    remote?.update(nowMs);

    // 6. camera rig from the predicted eye, and the sun shadow frustum around it
    if (livePrediction !== undefined) {
      livePrediction.eyePosition(eyeScratch);
      stage.camera.position.set(eyeScratch.x, eyeScratch.y, eyeScratch.z);
      stage.camera.rotation.set(input.pitch, input.yaw, 0, "YXZ");
      sunTarget.set(eyeScratch.x, eyeScratch.y, eyeScratch.z);
      followSun(stage.sun, sunTarget, viewDistanceChunks * CHUNK_EDGE_M);
      livePrediction.renderPosition(feetScratch);
      if (playerId !== "") {
        playerRenderer.upsert(playerId, feetScratch, input.yaw, true);
      }
    }

    // 7. HUD (internally limited to 4 Hz, dirty-flagged)
    hud.update(
      {
        fps,
        ping: net?.ping ?? 0,
        serverTick,
        players: remote?.latestPlayers ?? 0,
        position: feetScratch,
        chunks: liveWorld?.chunkCount ?? 0,
        meshed: liveWorld?.meshedCount ?? 0,
        pendingEdits: liveWorld?.pendingCount ?? 0,
        mesherQueue: liveWorld?.mesherQueued ?? 0,
        mesherInFlight: liveWorld?.inFlightCount ?? 0,
        mesherWorkers: pool.workers,
        tier: renderer.capabilities.isWebGL2 ? "webgl2" : "webgl1",
        transport: net?.kind ?? "none",
        status: net?.status ?? "disconnected",
        correction: livePrediction?.correctionM ?? 0,
        locked: input.isLocked,
        selectedBlock: HOTBAR_BLOCKS[selectedSlot] ?? 0,
      },
      nowMs,
    );

    // 8. one render
    renderer.render(stage.scene, stage.camera);
  }

  function shutdown(): void {
    running = false;
    if (rafId !== 0) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
    window.removeEventListener("pagehide", shutdown);
    net?.close();
    net = undefined;
    input.detach();
    remote?.clear();
    world?.dispose();
    pool.dispose();
    chunkRenderer.dispose();
    playerRenderer.dispose();
    atlas.dispose();
    disposeRenderer(renderer);
    hud.dispose();
  }
  window.addEventListener("pagehide", shutdown);

  // `NetClient` is the one subsystem in the boot order that has to be TOLD to start: constructing it
  // only stores the config, and nothing else ever calls `connect()`. Placing the call here - after
  // every `net.on(...)` registration above, and immediately before the first frame - is what makes the
  // first `welcome`/`chunk` frames dispatchable: starting earlier would let the authority's handshake
  // reach a transport whose handler set is still empty, and those frames are never re-sent.
  net.connect();

  rafId = window.requestAnimationFrame(frame);
}

/** Voxel-vs-local-body test for placement (PLAYER_RADIUS_M / PLAYER_HEIGHT_M from @rogueworlds/sim). */
function intersectsLocalBody(bx: number, by: number, bz: number, feet: Vec3): boolean {
  const minX = feet.x - PLAYER_RADIUS_M;
  const maxX = feet.x + PLAYER_RADIUS_M;
  const minZ = feet.z - PLAYER_RADIUS_M;
  const maxZ = feet.z + PLAYER_RADIUS_M;
  const minY = feet.y;
  const maxY = feet.y + PLAYER_HEIGHT_M;
  const voxelMinX = bx * VOXEL_SIZE_M;
  const voxelMinY = by * VOXEL_SIZE_M;
  const voxelMinZ = bz * VOXEL_SIZE_M;
  const voxelMaxX = voxelMinX + VOXEL_SIZE_M;
  const voxelMaxY = voxelMinY + VOXEL_SIZE_M;
  const voxelMaxZ = voxelMinZ + VOXEL_SIZE_M;
  return (
    voxelMinX < maxX &&
    voxelMaxX > minX &&
    voxelMinY < maxY &&
    voxelMaxY > minY &&
    voxelMinZ < maxZ &&
    voxelMaxZ > minZ
  );
}

async function loadConfig(): Promise<RemoteConfig> {
  try {
    const response = await fetch("/api/config", { headers: { accept: "application/json" } });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const payload = (await response.json()) as unknown;
    const parsed = parseConfig(payload);
    if (parsed !== undefined) {
      if (parsed.protocolVersion !== PROTOCOL_VERSION) {
        console.warn(
          `[client] /api/config reports protocol v${parsed.protocolVersion}, this build speaks v${PROTOCOL_VERSION}`,
        );
      }
      return parsed;
    }
    console.warn("[client] /api/config is not a usable RemoteConfig");
  } catch (error) {
    console.warn(
      `[client] /api/config unavailable (${error instanceof Error ? error.message : String(error)}); ` +
        `falling back to ${DEV_FALLBACK_ENDPOINT}`,
    );
  }
  return FALLBACK_CONFIG;
}

function parseConfig(payload: unknown): RemoteConfig | undefined {
  if (payload === null || typeof payload !== "object") {
    return undefined;
  }
  const record = payload as Record<string, unknown>;
  const version = record["protocolVersion"];
  const endpoint = record["serverEndpoint"];
  const world = record["world"];
  if (typeof version !== "number" || typeof endpoint !== "string") {
    return undefined;
  }
  if (world === null || typeof world !== "object") {
    return undefined;
  }
  const worldRecord = world as Record<string, unknown>;
  const tickHz = worldRecord["tickHz"];
  const chunkSize = worldRecord["chunkSize"];
  const voxelSize = worldRecord["voxelSize"];
  const viewDistanceChunks = worldRecord["viewDistanceChunks"];
  const maxPlayers = worldRecord["maxPlayers"];
  if (
    typeof tickHz !== "number" ||
    typeof chunkSize !== "number" ||
    typeof voxelSize !== "number" ||
    typeof viewDistanceChunks !== "number" ||
    typeof maxPlayers !== "number"
  ) {
    return undefined;
  }
  return {
    protocolVersion: version,
    serverEndpoint: endpoint,
    world: { tickHz, chunkSize, voxelSize, viewDistanceChunks, maxPlayers },
  };
}

function playerName(): string {
  const fromQuery = new URLSearchParams(window.location.search).get("name");
  if (fromQuery !== null && fromQuery.trim() !== "") {
    return fromQuery.trim().slice(0, 24);
  }
  return "player";
}

function aspectOf(canvas: HTMLCanvasElement): number {
  const width = canvas.clientWidth > 0 ? canvas.clientWidth : window.innerWidth;
  const height = canvas.clientHeight > 0 ? canvas.clientHeight : window.innerHeight;
  if (width <= 0 || height <= 0) {
    return 16 / 9;
  }
  return width / height;
}

function requireElement<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) {
    throw new Error(`missing #${id} in index.html`);
  }
  return node as T;
}

void main().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const appRoot = document.getElementById("app");
  if (appRoot !== null) {
    appRoot.replaceChildren();
    const overlay = document.createElement("div");
    overlay.className = "overlay bad";
    const title = document.createElement("div");
    title.className = "t";
    title.textContent = "Client failed to start";
    const detail = document.createElement("div");
    detail.className = "d";
    detail.textContent = message;
    overlay.append(title, detail);
    appRoot.append(overlay);
  }
  console.error("[client] boot failed:", error);
});
